import React from 'react';

import { Money } from '@betterment/core';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Box } from '@betterment/samba-react';
import { AccountSettingsModalContent, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CloseAccountModal } from './components/close-account/CloseAccountModal';
import { DeletePurposeConfirmation } from './components/DeletePurposeConfirmation';
import { EnvelopeAccountSettingsModalDocument } from './graphql/accountSettingsModal.generated';
import { UpdateAccountNameDocument } from './graphql/mutation.updateAccountName.generated';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { ModalSectionLoading } from '../../../Shared/components-v2/modal-section/components/ModalSectionLoading';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { isType } from '../../../Shared/utils/typeGuards';

import type {
  AccountSettingsAccount,
  CloseAccountActionProps,
  DeletePurposeActionProps,
  ManagedAccount,
  RenderCloseAccountAction,
  RenderDeletePurposeAction,
} from '@betterment/shared-components';

export const AccountSettingsModalWrapper: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const { data, loading, refetch } = useGraphQLQuery(EnvelopeAccountSettingsModalDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });
  const [updateAccountName] = useGraphQLMutation(UpdateAccountNameDocument);
  const handleNameUpdate = async (nameOverride?: string) => {
    await updateAccountName({
      variables: {
        input: {
          accountId,
          nameOverride,
        },
      },
    });
    await refetch();
  };

  if (loading) {
    return (
      <Box css={{ display: 'grid', gap: 'space.200', mt: 'space.200' }}>
        <ModalSectionLoading />
        <ModalSectionLoading />
        <ModalSectionLoading />
      </Box>
    );
  }

  if (!data?.envelopeAccount) return null;

  const { envelopeAccount } = data;
  const currentPath = urlBuilder.appAccount.absolute({ accountId });

  const isCashOrManagedAccount = (account: typeof envelopeAccount) =>
    isType(account, 'CashAccount') || isType(account, 'ManagedAccount');

  const supportsCloseAccount = (account: typeof envelopeAccount) =>
    isCashOrManagedAccount(account) || isType(account, 'DirectTradingAccount');

  const accountSettingsUrlBuilders: {
    legalAccounts: string;
    editPurpose?: JSX.Element;
    editPortfolioStrategy: (args: { account: ManagedAccount; currentPath: string }) => string;
    editAllocation: (args: { account: ManagedAccount; currentPath: string }) => string;
    editExternallyAdvisedPlan: (args: { account: ManagedAccount; currentPath: string }) => string;
    editAutoAdjust: (args: { account: ManagedAccount; currentPath: string }) => string;
    taxCoordinationChange: (args: { account: ManagedAccount; currentPath: string }) => string;
    taxCoordinationSetup: () => string;
  } = {
    legalAccounts: urlBuilder.getLegalAccounts.absolute(),
    editPurpose:
      isCashOrManagedAccount(envelopeAccount) &&
      !(isType(envelopeAccount, 'ManagedAccount') && envelopeAccount.portfolio?.isCryptoPortfolio) &&
      envelopeAccount.legacyGoalId ? (
        <SambaAutoLink
          to={urlBuilder.appGoalsEditPurpose.absolute({
            goal_id: envelopeAccount.legacyGoalId,
            return_to: currentPath,
          })}
        >
          Add to a goal
        </SambaAutoLink>
      ) : undefined,
    editPortfolioStrategy: ({ account, currentPath: path }) =>
      account.legacyGoalId
        ? urlBuilder.appGoalsEditPortfolioStrategy.absolute({ goalId: account.legacyGoalId, return_to: path })
        : '',
    editAllocation: ({ account, currentPath: path }) =>
      account.legacyGoalId
        ? urlBuilder.appGoalsEditAllocation.absolute({ goal_id: account.legacyGoalId, return_to: path })
        : '',
    editExternallyAdvisedPlan: ({ account, currentPath: path }) =>
      account.legacyGoalId
        ? urlBuilder.appGoalsEditAllocation.absolute({ goal_id: account.legacyGoalId, return_to: path })
        : '',
    editAutoAdjust: ({ account, currentPath: path }) =>
      account.legacyGoalId
        ? urlBuilder.appGoalsEditAllocation.absolute({ goal_id: account.legacyGoalId, return_to: path })
        : '',
    taxCoordinationChange: ({ account, currentPath: path }) =>
      account.legacyGoalId
        ? urlBuilder.appGoalTaxLocationChangeNew.absolute({ goalId: account.legacyGoalId, return_to: path })
        : '',
    taxCoordinationSetup: () => urlBuilder.getTaxLocationSetup.absolute(),
  };

  let renderDeletePurpose: RenderDeletePurposeAction | undefined;
  if (isCashOrManagedAccount(envelopeAccount)) {
    renderDeletePurpose = (props: DeletePurposeActionProps) => (
      <DeletePurposeConfirmation
        accountId={props.accountId}
        accountName={props.accountName}
        purposeId={props.purposeId}
        purposeName={props.purposeName}
        variant="AccountSettings"
      />
    );
  }

  let renderCloseAccount: RenderCloseAccountAction | undefined;
  if (supportsCloseAccount(envelopeAccount)) {
    renderCloseAccount = (props: CloseAccountActionProps) => {
      return (
        <CloseAccountModal
          balance={Money(props.balance.cents)}
          purposeName={props.purposeName}
          name={props.name}
          legacyAccountId={props.legacyAccountId}
          triggerText="Close account"
        />
      );
    };
  }

  return (
    <IdempotencyProviderWithFeatureRegistry featureName="deletePurpose">
      <AccountSettingsModalContent
        account={envelopeAccount as AccountSettingsAccount}
        currentPath={currentPath}
        urlBuilders={accountSettingsUrlBuilders}
        renderDeletePurpose={renderDeletePurpose}
        renderCloseAccount={renderCloseAccount}
        handleNameUpdate={handleNameUpdate}
      />
    </IdempotencyProviderWithFeatureRegistry>
  );
};
